import React, { useEffect, useState } from 'react';
import { DATABASE_TYPES, ROUTE } from 'constant';
import { actions } from 'redux/actions';
import Shared from 'screens/shared';
import { useSelector } from 'react-redux';
import { ACTIONS_TYPES } from 'redux/helper';
import { isEmptyArray, _t, pushHistory } from 'utils';
import { form, formField } from 'types';
import { getLocaleDateTime } from 'utils/time';
import { getMinDateTimeForBackup } from 'utils/database';
import UpdateForm from './update-form';

export default ({ match, history }) => {
  const clusterId = match?.params?.id;

  const {
    [ACTIONS_TYPES.DATABASE.POSTGRESQL.GET]: database = {},
    [ACTIONS_TYPES.DATABASE.POSTGRESQL.GET_ALL_BACKUPS_BY_CLUSTER_ID]: backups = [],
    [ACTIONS_TYPES.DATA_CENTER.GET_ALL]: dataCenters = [],
  } = useSelector((e) => e);

  const [isConfirm, toggleConfirm] = useState(false);
  const [backupValues, setBackupValues] = useState(false);


  useEffect(() => {
    if (clusterId) {
      actions.database.postgresql.get({ id: clusterId });
      actions.database.postgresql.getAllBackupsByClusterId({ clusterId });
      if (isEmptyArray(dataCenters)) actions.dataCenter.getAll({});
    }
    // eslint-disable-next-line
  }, [clusterId]);

  const deleteHandler = () => {
    actions.database.postgresql.delete({ id: clusterId });
    pushHistory({ history, path: ROUTE.DASHBOARD.DATABASES.path });
  };

  const getDataCenterName = (dcId) => {
    const { properties = {} } = isEmptyArray(dataCenters) ? {} : dataCenters.find(({ id }) => id === dcId) || {};
    return properties.name;
  };

  const monitorClickHandler = () => {
    pushHistory({ history, path: ROUTE.DASHBOARD.DATABASE_MONITORING.path.replace(':id', clusterId) });
  };

  const onBackupRequested = (values, item) => {
    setBackupValues({ backupId: item?.id, recoveryTargetTime: values.recoveryTargetTime });
    toggleConfirm(true);
  };

  const onBackupSubmit = (valid) => {
    if (valid) {
      const { backupId, recoveryTargetTime } = backupValues;
      actions.database.postgresql.restoreBackup({ clusterId, backupId, recoveryTargetTime });
    }
    toggleConfirm(false);
  };

  const dcAndLan = (dcId, lanId) => {
    const { properties: dcProps = {} } = dataCenters.find((item) => item.id === dcId) || {};

    return (
      <>
        <span>
          <Shared.widgets.Icon name="boxes" style={{ marginRight: 5 }} />
          {dcProps.name || dcId}
        </span>
        <span style={{ marginLeft: '4px', marginRight: '4px' }}>
          | LAN:
        </span>
        <span>
          {lanId}
        </span>
      </>
    );
  };

  return (
    <Shared.widgets.Page
      route={ROUTE.DASHBOARD.DATABASES}
      title={_t('DATABASES.DATABASE')}
      strapIcon="database"
      strapTitle={database?.properties?.displayName}
      strapSubTitle={<Shared.utilities.Status state={database?.metadata?.state} className="m-t-5" />}
      onDelete={deleteHandler}
      editForm={<UpdateForm values={database} />}
      editAction={ACTIONS_TYPES.DATABASE.POSTGRESQL.UPDATE}
    >
      <Shared.widgets.Properties
        title={_t('DATABASES.PROPERTIES.TITLE')}
        properties={[
          ['UUID', { value: clusterId }],
          [_t('DATABASES.PROPERTIES.MONITOR_DATABASES'), (
            <span className="color-link pointer" onClick={monitorClickHandler}>
              <Shared.widgets.Icon name="chart-line" />
              <span className="m-l-5">{database?.properties?.displayName}</span>
            </span>
          )],
          [_t('DATABASES.PROPERTIES.LOCATION'), <Shared.utilities.Location location={database?.properties?.location} />],
          [_t('DATABASES.PROPERTIES.DNS_NAME'), database?.properties?.dnsName],
          [_t('DATABASES.FORM_FIELDS.BACKUP_LOCATION.TITLE'), database?.properties?.backupLocation],
          ['Postgres Version', { prefix: 'v', value: database?.properties?.postgresVersion }],
          [_t('DATABASES.PROPERTIES.INSTANCES'), database?.properties?.instances],
          [
            _t('DATABASES.PROPERTIES.SYNCHRONIZATION_MODE'),
            _t(`DATABASES.FORM_FIELDS.SYNCHRONIZATION_MODE.OPTIONS.${database?.properties?.synchronizationMode}`),
          ],
          [_t('DATABASES.PROPERTIES.CORE_COUNT'), { value: database?.properties?.cores }],
          [_t('DATABASES.PROPERTIES.RAM_SIZE'), { value: database?.properties?.ram / 1024, suffix: ' GB' }],
          [_t('DATABASES.PROPERTIES.STORAGE'), { value: database?.properties?.storageSize / 1024, suffix: ` GB (${database?.properties?.storageType})` }],
          [<span style={{ fontSize: 16, color: 'var(--color-base)' }}>{_t('DATABASES.PROPERTIES.MAINTENANCE_WINDOW')}</span>],
          [_t('DATABASES.PROPERTIES.TIME'), database?.properties?.maintenanceWindow?.time],
          [_t('DATABASES.PROPERTIES.WEEKDAY'), _t(`days.${database?.properties?.maintenanceWindow?.dayOfTheWeek.toLowerCase()}`)],
          [<span style={{ fontSize: 16, color: 'var(--color-base)' }}>{_t('DATABASES.PROPERTIES.CONNECTIONS')}</span>],
          ...(database?.properties?.connections || []).map((connection) => (
            [connection.cidr, dcAndLan(connection.datacenterId, connection.lanId)]
          )),
          [<Shared.billing.DBaaSPriceEstimation
            databaseType={DATABASE_TYPES.POSTGRES}
            model={{
              ...database?.properties,
              ram: database?.properties?.ram / 1024,
              storageSize: database?.properties?.storageSize / 1024,
            }}
          />],
        ]}
      />

      <Shared.widgets.Section
        sectionTitle={_t('DATABASES.CLUSTER_TO_DC_CONNECTION.TITLE')}
        itemIcon="boxes"
        items={database?.properties?.connections || []}
        setItemTitle={(e) => getDataCenterName(e.datacenterId)}
        setItemDetails={(e) => (
          <>
            <span>{`LAN: ${e.lanId}`}</span>
            <Shared.utilities.Separator />
            <span>{`IP: ${e.cidr}`}</span>
          </>
        )}
        style={{ margin: '20px 0' }}
        tableColumns={['', 'VDC', 'LAN / IP\'s']}
      />

      <Shared.widgets.Section
        sectionTitle={_t('DATABASES.BACKUPS.TITLE')}
        itemIcon="layer-group"
        items={backups}
        setItemTitle={(e) => (<div>Version: {e.properties.version}</div>)} // eslint-disable-line
        setItemDetails={(e) => (
          <span>
            <div>
              {`${_t('DATABASES.BACKUPS.CREATED')}: ${getLocaleDateTime({ date: e?.metadata?.createdDate })}`}
            </div>
            {
              e.properties.earliestRecoveryTargetTime
                ? (
                  <>
                    <div>
                      {_t('DATABASES.BACKUPS.EARLIEST_RECOVERY_TARGET_TIME')}
                      :
                    </div>
                    <div>{getLocaleDateTime({ date: e.properties.earliestRecoveryTargetTime })}</div>
                  </>
                )
                : ''
            }
          </span>
        )}
        actionForm={{
          title: _t('DATABASES.RESTORE.TITLE'),
          formProps: (item) => {
            const minDateTime = getMinDateTimeForBackup({ backup: item });

            return form({
              fieldsValues: {
                recoveryTargetTime: new Date(),
              },
              fields: [
                formField.mobileDateTime({
                  title: _t('DATABASES.BACKUPS.BACKUP_TIME'), name: 'recoveryTargetTime', maxDateTime: new Date(), minDateTime,
                }),
              ],
              onSubmit: onBackupRequested,
            });
          },
        }}
        tableColumns={['', 'Name', '', '']}
      />

      <Shared.dialog.Confirm show={isConfirm} onClose={onBackupSubmit} title={_t('SHARED.CONFIRM_PASSWORD.TITLE')} typeText="RESTORE" isPassword />

    </Shared.widgets.Page>
  );
};
